import React, { useState } from 'react';
import { AlertTriangle, X, Wrench } from 'lucide-react';
import { storage } from '../services/storage';
import { useModal } from './ui/ModalProvider';

interface MaintenanceBannerProps {
  onMaintenanceDisabled: () => void;
}

export const MaintenanceBanner: React.FC<MaintenanceBannerProps> = ({ onMaintenanceDisabled }) => {
  const { toast, confirm } = useModal();
  const [disabling, setDisabling] = useState(false);

  const handleDisableMaintenance = async () => {
    const confirmed = await confirm({
      title: "Désactiver le mode maintenance ?",
      message: "Les utilisateurs pourront à nouveau accéder à l'application. Continuer ?",
      confirmText: "Désactiver",
      cancelText: "Annuler"
    });

    if (!confirmed) return;

    setDisabling(true);
    try {
      const { error } = await storage.updateSystemConfig('maintenance_mode', {
        enabled: false,
        message: "L'application est actuellement en maintenance. Veuillez réessayer plus tard."
      });

      if (error) {
        toast.error('Erreur lors de la désactivation du mode maintenance: ' + (error.message || 'Erreur inconnue'));
        return;
      }

      toast.success('Mode maintenance désactivé');
      onMaintenanceDisabled();
    } catch (error: any) {
      toast.error('Erreur lors de la désactivation: ' + (error.message || 'Erreur inconnue'));
    } finally {
      setDisabling(false);
    }
  };

  return (
    <div className="bg-red-600 text-white px-4 py-3 flex items-center justify-between shadow-lg z-50 relative">
      <div className="flex items-center gap-3">
        <AlertTriangle className="w-5 h-5 shrink-0 animate-pulse" />
        <div className="flex items-center gap-2">
          <Wrench className="w-4 h-4" />
          <span className="font-semibold text-sm">
            Mode maintenance actif
          </span>
          <span className="text-red-100 text-xs hidden sm:inline">
            - Les utilisateurs voient la page de maintenance
          </span>
        </div>
      </div>
      <button
        onClick={handleDisableMaintenance}
        disabled={disabling}
        className="bg-white text-red-600 hover:bg-red-50 px-4 py-1.5 rounded-lg font-bold text-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 shrink-0"
      >
        {disabling ? (
          <>
            <div className="w-4 h-4 border-2 border-red-600 border-t-transparent rounded-full animate-spin"></div>
            <span>Désactivation...</span>
          </>
        ) : (
          <>
            <X className="w-4 h-4" />
            <span>Désactiver</span>
          </>
        )}
      </button>
    </div>
  );
};
